import React, { useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../ReaduxToolkit/Hooks';
import { addElement, updateElement, updateElements } from '../store/formDocumentSlice';
import { setSelection, setActiveCell } from '../store/formDesignerUiSlice';
import ElementRenderer from './ElementRenderer';
import SelectionLayer from './SelectionLayer';
import { PAGE_SIZE_PX, ElementType, ContentRun, FormElement } from '../store/types';
import { createElementDefaults } from '../elementRegistry';
import { appendRuns, insertVariableRunAtOffset } from '../contentRuns';
import { setCellContent } from '../tableUtils';
import { findYesNoGroup } from '../fieldUsage';

export const TOOLBOX_DRAG_TYPE = 'application/x-fd-toolbox-type';
export const VARIABLE_DRAG_TYPE = 'application/x-fd-variable';
// The payload of the drag in progress, kept in memory as well as in dataTransfer: browsers/extensions
// can hand back an empty dataTransfer on drop, and the drop then silently does nothing.
export const dragState: { variable: string; toolbox: string } = { variable: '', toolbox: '' };

const CONTENT_BEARING_TYPES = new Set(['text', 'label', 'paragraph', 'heading', 'pagenumber', 'textbox', 'textarea', 'checkbox', 'radio']);

// Elements a Checkbox/Radio tool click may turn into a checkbox/radio in place.
// Structural/media types are excluded since converting them would drop content
// (a table's cells, an image's src, ...) the user almost certainly wants kept.
const NOT_ATTACHABLE_TYPES = new Set(['table', 'image', 'rect', 'hline', 'vline']);

// Best-effort label for the field being attached: prefer a bound dynamic
// field's name, then an existing checkbox/radio label, then whatever plain
// text/variable the element already shows.
function elementTitle(el: FormElement): string {
  if (el.bindings?.dynamicField) return el.bindings.dynamicField;
  if (el.typeProps?.checkbox?.label) return el.typeProps.checkbox.label;
  if (el.typeProps?.radio?.label) return el.typeProps.radio.label;
  if (el.content && el.content.length > 0) {
    const variableRun = el.content.find((r) => r.type === 'variable');
    if (variableRun && variableRun.type === 'variable') return variableRun.fieldKey;
    const text = el.content
      .map((r) => (r.type === 'text' ? r.value : ''))
      .join('')
      .trim();
    if (text) return text;
  }
  return '';
}

interface Props {
  pageId: string;
}

export default function PageCanvas({ pageId }: Props) {
  const dispatch = useAppDispatch();
  const page = useAppSelector((s) => s.FormDocument.present.pages.find((p) => p.id === pageId));
  const pageSize = useAppSelector((s) => s.FormDocument.present.pageSize);
  const orientation = useAppSelector((s) => s.FormDocument.present.orientation);
  const zoom = useAppSelector((s) => s.FormDesignerUi.zoom);
  const selectedIds = useAppSelector((s) => s.FormDesignerUi.selectedIds);
  const activeTool = useAppSelector((s) => s.FormDesignerUi.activeTool);
  const canvasRef = useRef<HTMLDivElement>(null);

  if (!page) return null;

  const base = PAGE_SIZE_PX[pageSize];
  const pageWidth = orientation === 'landscape' ? base.height : base.width;
  const pageHeight = orientation === 'landscape' ? base.width : base.height;

  // Attach the armed Checkbox/Radio tool to an existing field: turn it into a
  // checkbox/radio in place, labeled with whatever title it already showed
  // (or with `labelOverride`, when the caller already knows the field name --
  // e.g. a field dragged from the palette straight onto the tool). The tool
  // stays armed afterward -- so a whole list of fields can be converted one
  // after another -- until the user explicitly turns it off (click the tool
  // button again, pick the other tool, or press Escape).
  const attachToolToElement = (el: FormElement, labelOverride?: string) => {
    if (!activeTool || el.locked || NOT_ATTACHABLE_TYPES.has(el.type)) return;
    const label = labelOverride || elementTitle(el) || (activeTool === 'checkbox' ? 'Checkbox label' : 'Option');
    dispatch(
      updateElement({
        pageId,
        id: el.id,
        changes:
          activeTool === 'checkbox'
            ? {
                type: 'checkbox',
                height: Math.max(el.height, 24),
                typeProps: { ...el.typeProps, checkbox: { ...el.typeProps?.checkbox, label, checked: el.typeProps?.checkbox?.checked ?? false } },
              }
            : {
                type: 'radio',
                height: Math.max(el.height, 24),
                typeProps: {
                  ...el.typeProps,
                  radio: {
                    ...el.typeProps?.radio,
                    label,
                    checked: el.typeProps?.radio?.checked ?? false,
                    groupName: el.typeProps?.radio?.groupName || '',
                  },
                },
              },
      })
    );
    dispatch(setSelection([el.id]));
  };

  // Create a brand-new checkbox/radio centered at (x, y), optionally pre-labeled
  // (used when a field was dropped somewhere that isn't an existing element).
  const createToolElement = (tool: 'checkbox' | 'radio', x: number, y: number, label?: string) => {
    const preview = createElementDefaults(tool, 0, 0);
    const newEl = createElementDefaults(tool, x - preview.width / 2, y - preview.height / 2);
    if (label) {
      newEl.typeProps =
        tool === 'checkbox'
          ? { checkbox: { ...newEl.typeProps?.checkbox!, label } }
          : { radio: { ...newEl.typeProps?.radio!, label } };
    }
    dispatch(addElement({ pageId, element: newEl }));
    dispatch(setSelection([newEl.id]));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;

    const toolboxType = e.dataTransfer.getData(TOOLBOX_DRAG_TYPE) || dragState.toolbox;
    const variableRaw = e.dataTransfer.getData(VARIABLE_DRAG_TYPE) || dragState.variable;
    const dropTargetEl = (e.target as HTMLElement).closest('.fd-element') as HTMLElement | null;
    const cellEl = (e.target as HTMLElement).closest('td[data-fd-cell-row]') as HTMLElement | null;

    const tableId = dropTargetEl?.dataset.elementId;
    const tableElement = tableId ? page.elements.find((item) => item.id === tableId) : null;
    const cell =
      cellEl && tableElement?.type === 'table' && tableElement.typeProps?.table
        ? { element: tableElement, table: tableElement.typeProps.table, row: Number(cellEl.dataset.fdCellRow), col: Number(cellEl.dataset.fdCellCol) }
        : null;

    const writeCellRuns = (runs: ContentRun[]) => {
      if (!cell) return;
      const updatedTable = setCellContent(cell.table, cell.row, cell.col, appendRuns(cell.table.cells[cell.row]?.[cell.col], runs));
      dispatch(
        updateElement({
          pageId,
          id: cell.element.id,
          changes: { typeProps: { ...cell.element.typeProps, table: updatedTable } },
        })
      );
      dispatch(setSelection([cell.element.id]));
      dispatch(setActiveCell({ elementId: cell.element.id, row: cell.row, col: cell.col }));
    };

    if (variableRaw) {
      const { fieldKey, sectionKey } = JSON.parse(variableRaw);
      const textToInsert = `$${fieldKey}$`;

      // Checkbox/Radio tool armed: dropping a field creates/converts it into
      // that control labeled with the field's own name, in one motion, instead
      // of dropping a plain text placeholder that would need a second click.
      if (activeTool && !cell) {
        const targetId = dropTargetEl?.dataset.elementId;
        const targetElement = targetId ? page.elements.find((item) => item.id === targetId) : null;
        if (targetElement && !targetElement.locked && !NOT_ATTACHABLE_TYPES.has(targetElement.type)) {
          attachToolToElement(targetElement, fieldKey);
        } else {
          createToolElement(activeTool, x, y, fieldKey);
        }
        return;
      }

      if (cell) {
        const cellRuns = cell.table.cells[cell.row]?.[cell.col] || [];
        const inputRunIdx = cellRuns.findIndex((r: any) => r.type === 'textbox' || r.type === 'textarea');
        if (inputRunIdx !== -1) {
          const updatedRuns = cellRuns.map((r: any, idx: number) =>
            idx === inputRunIdx ? { ...r, fieldKey } : r
          );
          const updatedTable = setCellContent(cell.table, cell.row, cell.col, updatedRuns);
          dispatch(
            updateElement({
              pageId,
              id: cell.element.id,
              changes: { typeProps: { ...cell.element.typeProps, table: updatedTable } },
            })
          );
        } else {
          writeCellRuns([{ type: 'variable', fieldKey }]);
        }
        return;
      }

      const targetId = dropTargetEl?.dataset.elementId;
      let targetElement = targetId ? page.elements.find((item) => item.id === targetId) : null;

      // If dropped onto canvas or locked container, find topmost unlocked input element at (x, y)
      if (!targetElement || targetElement.locked || (targetElement.type !== 'textbox' && targetElement.type !== 'textarea' && targetElement.type !== 'date')) {
        const hitInput = [...page.elements].reverse().find(
          (el) =>
            !el.locked &&
            (el.type === 'textbox' || el.type === 'textarea' || el.type === 'date') &&
            x >= el.x && x <= el.x + el.width &&
            y >= el.y && y <= el.y + el.height
        );
        if (hitInput) {
          targetElement = hitInput;
        }
      }

      if (targetElement) {
        if (targetElement.type === 'textbox' || targetElement.type === 'textarea' || targetElement.type === 'date') {
          dispatch(
            updateElement({
              pageId,
              id: targetElement.id,
              changes: {
                bindings: {
                  ...targetElement.bindings,
                  dynamicField: fieldKey,
                  sectionKey: sectionKey
                }
              },
            })
          );
          dispatch(setSelection([targetElement.id]));
          return;
        } else if (targetElement.type === 'checkbox' && !targetElement.locked) {
          // a Yes/No pair is one field: bind both boxes
          dispatch(
            updateElements({
              pageId,
              changes: findYesNoGroup(targetElement, page.elements).map((m) => ({
                id: m.id,
                changes: { bindings: { ...m.bindings, dynamicField: fieldKey, sectionKey } },
              })),
            })
          );
          dispatch(setSelection([targetElement.id]));
          return;
        } else if (targetElement.type === 'radio' && !targetElement.locked) {
          // a Yes/No pair is one field: bind both boxes
          dispatch(
            updateElements({
              pageId,
              changes: findYesNoGroup(targetElement, page.elements).map((m) => ({
                id: m.id,
                changes: { bindings: { ...m.bindings, dynamicField: fieldKey, sectionKey } },
              })),
            })
          );
          dispatch(setSelection([targetElement.id]));
          return;
        } else if (CONTENT_BEARING_TYPES.has(targetElement.type) && !targetElement.locked) {
          // Insert at the run/character the pointer is actually over -- not
          // always at the end -- so dropping onto e.g. "Patient Name: ____"
          // lands the field on that blank instead of jumping past whatever
          // else is later on the same line (see insertVariableRunAtOffset).
          const localX = x - targetElement.x;
          dispatch(
            updateElement({
              pageId,
              id: targetElement.id,
              changes: { content: insertVariableRunAtOffset(targetElement.content, fieldKey, localX, targetElement.style) },
            })
          );
          dispatch(setSelection([targetElement.id]));
          return;
        }
      }

      // If dropped in empty space not over any input box, create plain text element
      const estimatedWidth = Math.max(280, textToInsert.length * 9);
      const newEl = createElementDefaults('text', x, y);
      newEl.width = estimatedWidth;
      newEl.content = [{ type: 'variable', fieldKey }];
      newEl.bindings = { dynamicField: fieldKey };
      dispatch(addElement({ pageId, element: newEl }));
      dispatch(setSelection([newEl.id]));
      return;
    }

    if (toolboxType) {
      const type = toolboxType as ElementType;

      if (cell && CONTENT_BEARING_TYPES.has(type)) {
        if (type === 'checkbox') {
          writeCellRuns([{ type: 'text', value: '☐ Checkbox' }]);
        } else if (type === 'radio') {
          writeCellRuns([{ type: 'text', value: '○ Radio' }]);
        } else if (type === 'textbox' || type === 'textarea') {
          writeCellRuns([{ type: 'text', value: '[Text Box]' }]);
        } else {
          writeCellRuns(createElementDefaults(type, 0, 0).content || []);
        }
        return;
      }

      const preview = createElementDefaults(type, 0, 0);
      const newEl = createElementDefaults(type, x - preview.width / 2, y - preview.height / 2);
      dispatch(addElement({ pageId, element: newEl }));
      dispatch(setSelection([newEl.id]));
    }
  };

  return (
    <div style={{ width: pageWidth * zoom, height: pageHeight * zoom, position: 'relative', flex: '0 0 auto' }}>
      <div
        ref={canvasRef}
        className="fd-page"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: pageWidth,
          height: pageHeight,
          transform: `scale(${zoom})`,
          transformOrigin: 'top left',
          background: '#fff',
          boxShadow: '0 0 10px rgba(0,0,0,0.2)',
          cursor: activeTool ? 'crosshair' : undefined,
        }}
        onDragEnter={(e) => e.preventDefault()}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
        }}
        onDrop={handleDrop}
        onMouseDown={(e) => {
          if (e.target !== canvasRef.current) return;
          if (activeTool && canvasRef.current) {
            const rect = canvasRef.current.getBoundingClientRect();
            const x = (e.clientX - rect.left) / zoom;
            const y = (e.clientY - rect.top) / zoom;
            createToolElement(activeTool, x, y);
            return;
          }
          dispatch(setSelection([]));
        }}
      >
        {page.elements.map((el) => (
          <ElementRenderer
            key={el.id}
            element={el}
            pageId={pageId}
            isSelected={selectedIds.includes(el.id)}
            onMouseDown={(e) => {
              e.stopPropagation();
              if (activeTool) {
                attachToolToElement(el);
                return;
              }
              if (el.locked) return;
              if (e.shiftKey) {
                const next = selectedIds.includes(el.id)
                  ? selectedIds.filter((id) => id !== el.id)
                  : [...selectedIds, el.id];
                dispatch(setSelection(next));
              } else if (!selectedIds.includes(el.id)) {
                dispatch(setSelection([el.id]));
              }
            }}
          />
        ))}
        <SelectionLayer canvasRef={canvasRef} pageId={pageId} zoom={zoom} />
      </div>
    </div>
  );
}

